import React, { ReactNode, useState } from 'react';
import styled from '@emotion/styled';
import { CuratorsSuggestionsKey, ProjectsListData, useProcessList, useProjectsList } from './useProjectsList';
import { Buttons, Form, FormGrid, LoadingIndicator, OccupyFreeSpace, SearchField, SimpleDialog, useWindowHotkey } from '../../toolympus/components/primitives';
import { FormControlsForFields } from '../../toolympus/components/schemed';
import { Button, IconButton, Menu, MenuItem } from '@mui/material';
import { AddOutlined, Close, FilterList, ListOutlined, RefreshOutlined, SwapVert } from '@mui/icons-material';
import { ProjectList } from './ProjectsList';
import { AutoSuggest } from '../../toolympus/components/primitives/AutoSuggests';
import { CuratorsSelector } from './CuratorsSelector';
import { ProjectPanel } from './ProjectPanel';
import { ThemeSettings } from '../../theme';
import { useBrowserStoredValue } from '../../toolympus/hooks/useBrowserStoredValue';
import { ProjectStatusEditDialog } from './ProjectStatusEditDialog';
import { pluralizeRu } from '../../toolympus/api/pluralize';
import { DictionarySelectDisplay } from '../../toolympus/components/schemed/Select';
import { useDictionaries } from '../../toolympus/hooks/useDictionaries';
import { useOpenByIdFromUrl } from '../../toolympus/hooks/useOpenByIdFromUrl';

interface Props {
  title: string;
  itemTitle: string;
  newItemTitle: string;
  data: ProjectsListData;
}

export const ProjectsListPageBase = (props: Props) => {
  const { data } = props;
  const showStages = useBrowserStoredValue("t", undefined, "__p_show_stages");

  useWindowHotkey("alt+n", () => data.newRecord.startEditing());

  const dicts = useDictionaries();
  const support = dicts["ProjectSupportActors"];

  useOpenByIdFromUrl(
    "pid", "_id", data.data, p => data.editRecord.startEditing({ ...p }), data.editRecord.item?._id?.toString()
  )


  return (<>
    <Form
      title={props.title}
      className="project-page-form"
      fitFullHeight
      headerItems={<>
          <IconButton size="small" color="primary" onClick={() => data.reload()}>
            <RefreshOutlined />
          </IconButton>
          <OccupyFreeSpace />
          {data.isLoading && <LoadingIndicator sizeVariant="s" />}

          <CuratorsSelector
            options={data.extraFilters.curatorsOptions}
            selected={data.extraFilters.curators}
            update={data.extraFilters.setCurators}
            />
          <IconButton size="small" color="primary" className="add-new-btn" onClick={() => data.newRecord.startEditing()}><AddOutlined /></IconButton>
          <SearchField
            filter={data.filter.filter}
            setFilter={data.filter.setFilter}
            autoFocus
            noButton
            syncQuery
            />
      </>}>

      
      <FlyingButtons>
        <MiniButton
          title="Показывать промежуточные"
          className={`no-mobile ${showStages.value === "t" ? "active" : ""}`}
          onClick={() => showStages.update(showStages.value === "t" ? "f" : "t")}>
            <ListOutlined />
        </MiniButton>
        <SelectorMiniButton
          startIcon={<FilterList />}
          options={data.extraFilters.statesOptions}
          current={data.extraFilters.states.value}
          update={v => data.extraFilters.states.update(v)}
          />
        <SelectorMiniButton
          startIcon={<SwapVert />}
          options={data.extraFilters.sortingOptions}
          current={data.extraFilters.sorting.value}
          update={v => data.extraFilters.sorting.update(v)}
          />

        <MiniButton
          title="Фильтр"
          className={`${!!data.extraFilters.actors.current ? "active" : ""}`}
          onClick={() => data.extraFilters.actors.edit.startEditing()}>
            <FilterList />
        </MiniButton>

        {!!data.extraFilters.actors.current?.actor && !!data.extraFilters.actors.current?.actor_kind &&
          <TextNoticeButton
            className="no-mobile"
            onClick={() => data.extraFilters.actors.clear()}>
            <DictionarySelectDisplay
              field="actor_kind"
              schema={data.extraFilters.actors.schema.actor_kind}
              row={data.extraFilters.actors.current}
              />
            {": "}
            <span className="case-intact">
              {data.extraFilters.actors.current.actor_kind === "support"
                ? support?.records?.find(r => r.code === data.extraFilters.actors.current?.actor)?.label
                : (data.extraFilters.actors.current.actor || "...")}</span>
            {" "}
            <Close />
          </TextNoticeButton>}

        {!!data.payAttentionCount.data.total_records &&
          <TextNoticeButton
            alert
            className="no-mobile"
            onClick={() => data.extraFilters.states.update("pay-attention")}>
            {data.payAttentionCount.data.total_records}
            {" "}
            {data.mode === "process"
              ? pluralizeRu(data.payAttentionCount.data.total_records, "процесс", "процесса", "процессов")
              : pluralizeRu(data.payAttentionCount.data.total_records, "проект", "проекта", "проектов")}
            {" "}
            {pluralizeRu(data.payAttentionCount.data.total_records, "требует", "требуют", "требуют")} внимания
          </TextNoticeButton>}
      </FlyingButtons>

      <ProjectList
        mode={data.mode}
        items={data.data}
        startEditing={p => data.editRecord.startEditing({ ...p })}
        showStages={showStages.value === "t"}
        startEditingStatusCurrent={p => data.editStatus.start(p)}
        startEditingStatusPrev={p => data.editStatus.startPrev(p)}
        overloadedMembers={data.overloadedMembers}
        />
    </Form>

    <SimpleDialog
      dialogTitle={props.newItemTitle}
      noFullscreen
      submitOnModEnter
      {...data.newRecord.dialog}
      save={() => data.newRecord.save()}>

      {!!data.newRecord.item &&
        <FormGrid columns="1fr">
          <FormControlsForFields
            data={data.newRecord.item}
            onChange={(o,c) => data.newRecord.update(c)}
            schema={data.schema}
            errors={data.newRecord.errors}
            fields={[
              ["code", { controlProps: { autoFocus: true }}],
              ["title"],
              ["deadline"],
              ["curator", { control: AutoSuggest, suggestionsKey: CuratorsSuggestionsKey }],
              ["state", { autoComplete: true }],
            ]}
            />
        </FormGrid>}
      
    </SimpleDialog>


    <ProjectPanel
      data={data.editRecord}
      projectSchema={data.schema}
      stageSchema={data.stageSchema}
      statusSchema={data.statusSchema}
      remove={p => data.remove.run(p)}
      startEditingStatusCurrent={p => data.editStatus.start(p)}
      startEditingStatusPrev={p => data.editStatus.startPrev(p)}
      title={props.itemTitle}
      mode={data.mode}
      />

    <ProjectStatusEditDialog
      data={data.editStatus}
      />

    <SimpleDialog
      dialogTitle="Фильтр"
      saveLabel="применить"
      {...data.extraFilters.actors.edit.dialog}
      save={() => data.extraFilters.actors.edit.save()}
      submitOnModEnter
      noFullscreen
      simpleExtraActions={<>
        <Button onClick={() => data.extraFilters.actors.clear()}>сбросить</Button>
      </>}
      >
      {!!data.extraFilters.actors.edit.item &&
        <FormGrid columns="1fr" noMargin>
          <FormControlsForFields
            data={data.extraFilters.actors.edit.item}
            errors={data.extraFilters.actors.edit.errors}
            onChange={(o,c) => data.extraFilters.actors.edit.update(c)}
            schema={data.extraFilters.actors.schema}
            fields={[
              ["actor_kind", { autoComplete: true}],
              ["actor", { autoComplete: true, controlProps: { autoFocus: true } }],
            ]}
            />
        </FormGrid>}
    </SimpleDialog>
  </>);
}

export const ProjectsListPage = (props: {}) => {
  const data = useProjectsList();

  return (
    <ProjectsListPageBase
      title="Проекты"
      itemTitle="Проект"
      newItemTitle="Новый проект"
      data={data}
      />
  )
}


export const ProcessListPage = (props: {}) => {
  const data = useProcessList();

  return (
    <ProjectsListPageBase
      title="Процессы"
      itemTitle="Процесс"
      newItemTitle="Новый процесс"
      data={data}
      />
  )
}

interface SelectorProps {
  options?: { code: string, label: string}[];
  current?: string;
  update: (v: string) => void;
  startIcon?: ReactNode;
}

const SelectorMiniButton = (props: SelectorProps) => {
  const { options, current, update } = props;
  const [menuAnchor,setMenuAnchor] = useState<any>(null);

  const currentLabel = (options || []).find(o => o.code === current)?.label || "...";

  return (<>
    <TextMiniButton onClick={e => setMenuAnchor(e.target)}>
      {props.startIcon}{props.startIcon ? " " : ""}{currentLabel}
    </TextMiniButton>
    {(!!menuAnchor) &&
      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
        {(options || [])
          .map(option => (
            <MenuItem key={option.code} onClick={() => { update(option.code); setMenuAnchor(null);}}>{option.label}</MenuItem>
          ))}
      </Menu>}
  </>)
}


const MiniButton = styled.button`
  border: 1px solid ${ThemeSettings.colors.blue_grey}80;
  border-top: none;
  background: transparent;
  padding: 4px;
  height: 100%;
  display: flex;
  flex-flow: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  background: #ffffffa0;
  border-radius: 0 0 8px 8px;
  box-shadow: -1px 0px 10px -6px #000000ff;

  
  color: ${ThemeSettings.colors.blue_grey_dark};


  &.active {
    background: ${ThemeSettings.colors.primaryL1};
    color: ${ThemeSettings.colors.background};
  }

  & .MuiSvgIcon-root {
    font-size: 1rem;
  }

`;

const TextMiniButton = styled(MiniButton)`
  color: ${ThemeSettings.colors.blue_grey_dark};
  padding: 4px 8px;
  width: auto;
  white-space: nowrap;
  text-transform: lowercase;
  display: flex;
  flex-flow: row;
  align-items: center;
  justify-content: center;
  gap: 4px;
  cursor: pointer;

  &.active {
    background: ${ThemeSettings.colors.primaryL1};
    color: ${ThemeSettings.colors.background};
  }

  & .MuiSvgIcon-root {
    font-size: 1rem;
    color: ${ThemeSettings.colors.primary};
  }

`;

const TextNoticeButton = styled(MiniButton)<{ alert?: boolean }>`
  color: ${ThemeSettings.colors.blue_grey_dark};
  position: relative;
  padding: 4px 8px;
  padding-left: ${props => props.alert ? 16 : 8}px;
  width: auto;
  white-space: nowrap;
  text-transform: lowercase;
  display: flex;
  flex-flow: row;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  gap: 4px;
  
  background: none;
  border: none;
  box-shadow: none;
  outline: none;

  &:after {
    position: absolute;
    content: '';
    display: ${props => props.alert ? "block" : "none"};
    width: 12px;
    height: 12px;
    border-radius: 6px;
    background: ${ThemeSettings.colors.danger};

    left: 0;
    top: 50%;
    transform: translate(0,-60%);
  }

  & .case-intact {
    text-transform: none;
  }
`;

const FlyingButtons = styled(Buttons)`
  position: fixed;
  top: 0px;
  left: 280px;
  height: 26px; 

  flex-flow: row;
  overflow: hidden;
  gap: 12px;

  

  & ${MiniButton} + ${MiniButton} {
    border-top: 1px solid ${ThemeSettings.colors.border_light};
  }

  ${props => props.theme.breakpoints.down("sm")} {
    left: 20px;

    & .no-mobile {
      display: none;
    }
  }
`;
